import React, { useMemo } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const HEATMAP_CELL_MIN = 4;
const HEATMAP_CELL_MAX = 96;

function clampCellSize(px: number | undefined): number | undefined {
  if (px == null || Number.isNaN(px)) return undefined;
  const n = Math.round(px);
  if (n < HEATMAP_CELL_MIN || n > HEATMAP_CELL_MAX) return undefined;
  return n;
}

function parsePropertyDate(raw: unknown): Date | null {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const t = new Date(ms);
    return Number.isNaN(t.getTime()) ? null : t;
  }
  if (typeof raw === 'string' && String(raw).trim() !== '') {
    const t = new Date(raw);
    return Number.isNaN(t.getTime()) ? null : t;
  }
  return null;
}

interface CalendarHeatmapPanelProps {
  features: GeoJSON.Feature[];
  dateField?: string;
  weeks: number;
  chartTitle?: string;
  /** Fixed square size in px; omit for responsive columns (default). */
  cellSizePx?: number;
}

const CalendarHeatmapPanel: React.FC<CalendarHeatmapPanelProps> = ({
  features,
  dateField,
  weeks,
  chartTitle,
  cellSizePx: cellSizeProp,
}) => {
  const theme = useTheme();
  const cellSizePx = clampCellSize(cellSizeProp);
  const useFixedCells = cellSizePx != null;
  const gapPx = useFixedCells ? Math.max(2, Math.round(cellSizePx * 0.12)) : undefined;

  const cal = useMemo(() => {
    const totalDays = weeks * 7;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(start.getDate() - (totalDays - 1));

    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const counts = new Map<string, number>();
    for (let i = 0; i < totalDays; i++) {
      const dt = new Date(start);
      dt.setDate(start.getDate() + i);
      counts.set(dayKey(dt), 0);
    }

    if (dateField) {
      for (const f of features) {
        const props = f.properties as Record<string, unknown> | null | undefined;
        const raw = props?.[dateField];
        const t = parsePropertyDate(raw);
        if (!t) continue;
        t.setHours(0, 0, 0, 0);
        const k = dayKey(t);
        if (counts.has(k)) {
          counts.set(k, (counts.get(k) ?? 0) + 1);
        }
      }
    }

    const cells: { date: Date; key: string; count: number; weekday: number }[] = [];
    for (let i = 0; i < totalDays; i++) {
      const dt = new Date(start);
      dt.setDate(start.getDate() + i);
      const k = dayKey(dt);
      cells.push({
        date: dt,
        key: k,
        count: counts.get(k) ?? 0,
        /** Monday = 0 … Sunday = 6 */
        weekday: (dt.getDay() + 6) % 7,
      });
    }

    const max = Math.max(1, ...cells.map((c) => c.count));
    return { cells, weeks, max, start, end: today };
  }, [features, dateField, weeks]);

  const padded = useMemo(() => {
    const { cells } = cal;
    if (!cells.length) return [] as (typeof cells[0] | null)[];
    const pad = cells[0].weekday;
    const out: (typeof cells[0] | null)[] = [...Array(pad).fill(null), ...cells];
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [cal]);

  const rows = useMemo(() => {
    const r: (typeof padded)[] = [];
    for (let i = 0; i < padded.length; i += 7) {
      r.push(padded.slice(i, i + 7));
    }
    return r;
  }, [padded]);

  const { max } = cal;

  const colorFor = (count: number) => {
    if (count <= 0) return theme.palette.action.hover;
    const t = count / max;
    if (t < 0.25) return theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.35)' : 'rgba(46, 125, 50, 0.25)';
    if (t < 0.5) return theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.55)' : 'rgba(46, 125, 50, 0.45)';
    if (t < 0.75) return theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.75)' : 'rgba(46, 125, 50, 0.65)';
    return theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.95)' : 'rgba(27, 94, 32, 0.85)';
  };

  const cornerRadius = useFixedCells ? Math.max(2, Math.min(8, cellSizePx * 0.15)) : 0.5;

  /** Auto = fluid squares; fixed px = slider-controlled size (intrinsic grid width, centered in tile). */
  const cellSxFilled = {
    width: useFixedCells ? cellSizePx : '100%',
    height: useFixedCells ? cellSizePx : undefined,
    aspectRatio: useFixedCells ? undefined : ('1' as const),
    borderRadius: useFixedCells ? `${cornerRadius}px` : 0.5,
    border: 1,
    borderColor: 'divider',
    minWidth: useFixedCells ? undefined : 8,
    minHeight: useFixedCells ? undefined : 8,
  };

  const cellSxEmpty = {
    width: useFixedCells ? cellSizePx : '100%',
    height: useFixedCells ? cellSizePx : undefined,
    aspectRatio: useFixedCells ? undefined : ('1' as const),
    borderRadius: useFixedCells ? `${cornerRadius}px` : 0.5,
  };

  const fixedGridWidth = useFixedCells ? 7 * cellSizePx + 6 * (gapPx ?? 0) : undefined;

  if (!features.length || !dateField) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          No date field configured. Bind a Web Map layer with a date field in widget settings.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      sx={{
        width: '100%',
        height: 'auto',
        maxHeight: '100%',
        overflow: 'auto',
        p: 0.5,
        minHeight: 0,
        alignSelf: 'flex-start',
      }}
      spacing={0.5}
    >
      {chartTitle && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>
          {chartTitle}
        </Typography>
      )}
      <Typography variant="caption" color="text.disabled" sx={{ px: 0.25 }}>
        Reports by local day (last {cal.weeks} weeks)
      </Typography>
      <Box
        sx={
          useFixedCells
            ? {
                display: 'grid',
                gridTemplateColumns: `repeat(7, ${cellSizePx}px)`,
                columnGap: `${gapPx}px`,
                rowGap: `${gapPx}px`,
                width: fixedGridWidth,
                maxWidth: '100%',
                alignSelf: 'center',
                flexShrink: 0,
              }
            : {
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 0.35,
                width: '100%',
                maxWidth: '100%',
                alignSelf: 'stretch',
              }
        }
      >
        {DOW.map((d) => (
          <Typography
            key={d}
            variant="caption"
            color="text.disabled"
            sx={{
              textAlign: 'center',
              fontSize: useFixedCells ? `${Math.max(9, Math.min(12, cellSizePx * 0.28))}px` : '0.6rem',
              lineHeight: 1.2,
              alignSelf: 'end',
              pb: useFixedCells ? 0.25 : 0,
            }}
          >
            {d}
          </Typography>
        ))}
        {rows.flatMap((week, wi) =>
          week.map((cell, di) => {
            const key = `${wi}-${di}`;
            if (!cell) {
              return <Box key={key} sx={cellSxEmpty} />;
            }
            return (
              <Box
                key={cell.key}
                title={`${cell.date.toLocaleDateString()}: ${cell.count} reports`}
                sx={{
                  ...cellSxFilled,
                  bgcolor: colorFor(cell.count),
                }}
              />
            );
          })
        )}
      </Box>
    </Stack>
  );
};

export default CalendarHeatmapPanel;
